import { MouseEvent, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Link,
    List,
    ListItemButton,
    ListItemText,
    Menu,
    MenuItem,
    Snackbar,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FolderIcon from '@mui/icons-material/Folder';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCanAccess, useDataProvider, useNotify, useStore } from 'react-admin';
import { QuoteAttachmentPreviewDialog } from '@/components/QuoteAttachmentPreviewDialog';
import { CrmDataProvider } from '@/providers/types';
import {
    MapSharePointFilesResult,
    QuoteAttachment,
    QuoteDriveFolderNode,
    QuoteDriveItem,
    downloadQuoteAttachment,
    driveItemToQuoteAttachment,
    driveZipDownloadFilename,
    fetchQuoteDriveThumbnailBlob,
    filterDriveItemsByName,
    invalidateQuoteDriveThumbnailCache,
    isImageAttachment,
    isPdfAttachment,
    triggerBlobDownload,
} from '@/utils/quote_attachment_utils';

type ViewMode = 'icons' | 'list';

type Props = {
    quoteId: number;
    readOnly?: boolean;
    /** When set, the explorer opens rooted at this OneDrive path instead of the quote's default folder. */
    defaultRootPath?: string;
    /** Label shown as the breadcrumb root instead of "Files" (e.g. "Files in Permits folder"). */
    rootLabel?: string;
};

type UploadConflict = {
    file: File;
    name: string;
};

const NAME_EXISTS_MSG = 'A file with that name already exists';

function fileWithName(file: File, name: string): File {
    if (file.name === name) return file;
    return new File([file], name, { type: file.type });
}

function FileThumb({
    item,
    quoteId,
    size = 48,
}: {
    item: QuoteDriveItem;
    quoteId: number;
    size?: number;
}) {
    const contentType = item.content_type || '';
    const isImage =
        isImageAttachment(contentType) || /\.(png|jpe?g)$/i.test(item.name);
    const [src, setSrc] = useState<string | null>(null);
    const [loading, setLoading] = useState(isImage);

    useEffect(() => {
        if (!isImage) {
            setSrc(null);
            setLoading(false);
            return undefined;
        }
        let objectUrl: string | null = null;
        let cancelled = false;
        setLoading(true);
        setSrc(null);
        fetchQuoteDriveThumbnailBlob(quoteId, item.path)
            .then(blob => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setSrc(objectUrl);
            })
            .catch(() => {
                if (!cancelled) setSrc(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [quoteId, item.path, isImage]);

    if (isPdfAttachment({ content_type: contentType, filename: item.name })) {
        return <PictureAsPdfIcon sx={{ fontSize: size, color: 'error.main' }} aria-hidden />;
    }
    if (isImage && loading) {
        const spinner = Math.max(14, Math.min(22, Math.round(size * 0.45)));
        return (
            <Box
                sx={
                    size >= 48
                        ? {
                              position: 'absolute',
                              inset: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                          }
                        : {
                              width: size,
                              height: size,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                          }
                }
            >
                <CircularProgress size={spinner} />
            </Box>
        );
    }
    if (isImage && src) {
        return (
            <Box
                component="img"
                src={src}
                alt=""
                sx={
                    size >= 48
                        ? {
                              position: 'absolute',
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                          }
                        : { width: size, height: size, objectFit: 'cover', borderRadius: 0.5 }
                }
            />
        );
    }
    if (isImage) {
        return <ImageIcon sx={{ fontSize: size, color: 'text.secondary' }} aria-hidden />;
    }
    return <InsertDriveFileIcon sx={{ fontSize: size, color: 'text.secondary' }} aria-hidden />;
}

function invalidMoveDest(item: QuoteDriveItem, dest: string): boolean {
    if (!item.is_folder) return false;
    return dest === item.path || dest.startsWith(`${item.path}/`);
}

function FolderTreePicker({
    nodes,
    selected,
    onSelect,
    disabledPath,
    depth = 0,
}: {
    nodes: QuoteDriveFolderNode[];
    selected: string;
    onSelect: (path: string) => void;
    disabledPath: (path: string) => boolean;
    depth?: number;
}) {
    return (
        <>
            {nodes.map(node => (
                <Box key={node.path}>
                    <ListItemButton
                        selected={selected === node.path}
                        disabled={disabledPath(node.path)}
                        onClick={() => onSelect(node.path)}
                        sx={{ pl: 2 + depth * 2 }}
                    >
                        <FolderIcon sx={{ mr: 1, color: 'warning.main' }} fontSize="small" />
                        <ListItemText primary={node.name} />
                    </ListItemButton>
                    {node.children.length > 0 && (
                        <FolderTreePicker
                            nodes={node.children}
                            selected={selected}
                            onSelect={onSelect}
                            disabledPath={disabledPath}
                            depth={depth + 1}
                        />
                    )}
                </Box>
            ))}
        </>
    );
}

function ItemMenu({
    item,
    allowDownload,
    allowUpdate,
    allowDelete,
    onDownload,
    onShare,
    onRename,
    onMove,
    onDelete,
    floating = true,
}: {
    item: QuoteDriveItem;
    allowDownload: boolean;
    allowUpdate: boolean;
    allowDelete: boolean;
    onDownload: () => void;
    onShare: () => void;
    onRename: () => void;
    onMove: () => void;
    onDelete: () => void;
    floating?: boolean;
}) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    if (!allowDownload && !allowUpdate && !allowDelete) return null;
    const allowShare = allowDownload && !item.is_folder;
    return (
        <>
            <IconButton
                size="small"
                aria-label="file actions"
                onClick={(event: MouseEvent<HTMLButtonElement>) => {
                    event.stopPropagation();
                    setAnchorEl(event.currentTarget);
                }}
                sx={floating ? { position: 'absolute', top: 4, right: 4 } : undefined}
            >
                <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
            >
                {allowDownload && (
                    <MenuItem
                        onClick={() => {
                            setAnchorEl(null);
                            onDownload();
                        }}
                    >
                        Download
                    </MenuItem>
                )}
                {allowShare && (
                    <MenuItem
                        onClick={() => {
                            setAnchorEl(null);
                            onShare();
                        }}
                    >
                        Share
                    </MenuItem>
                )}
                {allowUpdate && (
                    <MenuItem
                        onClick={() => {
                            setAnchorEl(null);
                            onRename();
                        }}
                    >
                        Rename
                    </MenuItem>
                )}
                {allowUpdate && (
                    <MenuItem
                        onClick={() => {
                            setAnchorEl(null);
                            onMove();
                        }}
                    >
                        Move to…
                    </MenuItem>
                )}
                {allowDelete && (
                    <MenuItem
                        onClick={() => {
                            setAnchorEl(null);
                            onDelete();
                        }}
                    >
                        Delete
                    </MenuItem>
                )}
            </Menu>
        </>
    );
}

export function QuoteDriveExplorer({ quoteId, readOnly = false, defaultRootPath, rootLabel }: Props) {
    const notify = useNotify();
    const dataProvider = useDataProvider<CrmDataProvider>();
    const queryClient = useQueryClient();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const folderInputRef = useRef<HTMLInputElement>(null);
    const uploadQueueRef = useRef<File[]>([]);
    const [path, setPath] = useState(defaultRootPath ?? '');
    const [view, setView] = useStore<ViewMode>('quotes.attachments.view', 'icons');
    const [preview, setPreview] = useState<QuoteAttachment | null>(null);
    const [folderName, setFolderName] = useState('');
    const [folderOpen, setFolderOpen] = useState(false);
    const [renameItem, setRenameItem] = useState<QuoteDriveItem | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const [moveItem, setMoveItem] = useState<QuoteDriveItem | null>(null);
    const [moveDest, setMoveDest] = useState('');
    const [deleteItem, setDeleteItem] = useState<QuoteDriveItem | null>(null);
    const [conflict, setConflict] = useState<UploadConflict | null>(null);
    const [conflictRename, setConflictRename] = useState(false);
    const [conflictName, setConflictName] = useState('');
    const [selectedPaths, setSelectedPaths] = useState<Set<string>>(() => new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [searchOpen, setSearchOpen] = useState(false);
    const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [progressMessage, setProgressMessage] = useState<string | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const dragCounterRef = useRef<number>(0);
    const [shareItem, setShareItem] = useState<QuoteDriveItem | null>(null);
    const [shareLink, setShareLink] = useState<string | null>(null);
    const [copySuccess, setCopySuccess] = useState(false);

    const { canAccess: canRead = false } = useCanAccess({
        resource: 'project_file',
        action: 'show',
    });
    const { canAccess: canCreate = false } = useCanAccess({
        resource: 'project_file',
        action: 'create',
    });
    const { canAccess: canUpdate = false } = useCanAccess({
        resource: 'project_file',
        action: 'edit',
    });
    const { canAccess: canDelete = false } = useCanAccess({
        resource: 'project_file',
        action: 'delete',
    });

    const driveQuery = useQuery({
        queryKey: ['quoteDrive', quoteId, path],
        queryFn: () => dataProvider.getQuoteDrive(quoteId, path),
        enabled: canRead,
    });
    const treeQuery = useQuery({
        queryKey: ['quoteDriveTree', quoteId],
        queryFn: () => dataProvider.getQuoteDriveTree(quoteId),
        enabled: canRead && moveItem != null,
    });

    const invalidate = () => {
        void queryClient.invalidateQueries({ queryKey: ['quoteDrive', quoteId] });
        void queryClient.invalidateQueries({ queryKey: ['quoteDriveTree', quoteId] });
    };

    const showProgress = (message: string) => {
        setProgressMessage(message);
    };

    const hideProgress = () => {
        setProgressMessage(null);
    };

    const refreshDriveList = async (progressMsg: string) => {
        showProgress(progressMsg);
        try {
            await queryClient.refetchQueries({ queryKey: ['quoteDrive', quoteId] });
            await queryClient.refetchQueries({ queryKey: ['quoteDriveTree', quoteId] });
        } finally {
            hideProgress();
        }
    };

    const items: QuoteDriveItem[] = driveQuery.data?.items ?? [];
    const visibleItems = filterDriveItemsByName(items, searchQuery);
    const canSelect = !readOnly && canRead;

    useEffect(() => {
        setSelectedPaths(new Set());
    }, [path]);

    useEffect(() => {
        if (!folderOpen) return undefined;
        const timer = window.setTimeout(() => {
            const input = folderInputRef.current;
            if (input) {
                input.focus();
                input.select();
            }
        }, 0);
        return () => window.clearTimeout(timer);
    }, [folderOpen]);

    const toggleSelected = (itemPath: string) => {
        setSelectedPaths(prev => {
            const next = new Set(prev);
            if (next.has(itemPath)) next.delete(itemPath);
            else next.add(itemPath);
            return next;
        });
    };

    const clearSelection = () => setSelectedPaths(new Set());

    const selectAllVisible = () => {
        setSelectedPaths(new Set(visibleItems.map(item => item.path)));
    };

    const handleDragEnter = (event: React.DragEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        dragCounterRef.current += 1;
        if (event.dataTransfer?.items && event.dataTransfer.items.length > 0) {
            setDragActive(true);
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDragLeave = (event: React.DragEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        dragCounterRef.current -= 1;
        if (dragCounterRef.current === 0) {
            setDragActive(false);
        }
    };

    const handleDrop = (event: React.DragEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        dragCounterRef.current = 0;
        setDragActive(false);

        const files = Array.from(event.dataTransfer?.files ?? []);
        if (files.length > 0) {
            showProgress('Uploading…');
            enqueueUploads(files);
        }
    };

    const existingFile = (name: string) =>
        items.find(item => !item.is_folder && item.name === name) ?? null;

    const openConflict = (file: File, name: string) => {
        setConflict({ file, name });
        setConflictRename(false);
        setConflictName(name);
    };

    const clearConflict = () => {
        setConflict(null);
        setConflictRename(false);
        setConflictName('');
    };

    const startUploadRef = useRef<(file: File, name: string, overwrite: boolean) => void>(
        () => undefined,
    );

    const drainUploadQueue = () => {
        const next = uploadQueueRef.current.shift();
        if (next) startUploadRef.current(next, next.name, false);
        else hideProgress();
    };

    const uploadMut = useMutation({
        mutationFn: ({ file, overwrite }: { file: File; overwrite: boolean }) =>
            dataProvider.uploadQuoteDriveFile(quoteId, path, file, { overwrite }),
        onSuccess: (_data, vars) => {
            const rel = path ? `${path}/${vars.file.name}` : vars.file.name;
            invalidateQuoteDriveThumbnailCache(quoteId, rel);
            clearConflict();
            invalidate();
            drainUploadQueue();
        },
        onError: (err: Error, vars) => {
            if (err.message === NAME_EXISTS_MSG && !vars.overwrite) {
                openConflict(vars.file, vars.file.name);
                return;
            }
            notify(err.message, { type: 'error' });
            clearConflict();
            drainUploadQueue();
        },
    });

    const startUpload = (file: File, name: string, overwrite: boolean) => {
        if (!overwrite && existingFile(name)) {
            openConflict(file, name);
            return;
        }
        uploadMut.mutate({ file: fileWithName(file, name), overwrite });
    };
    startUploadRef.current = startUpload;
    const folderMut = useMutation({
        mutationFn: (name: string) => dataProvider.createQuoteDriveFolder(quoteId, path, name),
        onSuccess: async () => {
            setFolderOpen(false);
            setFolderName('');
            await refreshDriveList('Fetching the new folder…');
        },
        onError: (err: Error) => notify(err.message, { type: 'error' }),
    });
    const submitCreateFolder = () => {
        const name = folderName.trim();
        if (!name || folderMut.isPending) return;
        folderMut.mutate(name);
    };
    const renameMut = useMutation({
        mutationFn: ({ item, name }: { item: QuoteDriveItem; name: string }) =>
            dataProvider.renameQuoteDriveItem(quoteId, item.path, name),
        onSuccess: (_data, vars) => {
            invalidateQuoteDriveThumbnailCache(quoteId, vars.item.path);
            setRenameItem(null);
            invalidate();
        },
        onError: (err: Error) => notify(err.message, { type: 'error' }),
    });
    const submitRename = () => {
        const name = renameValue.trim();
        if (!name || renameMut.isPending || !renameItem) return;
        renameMut.mutate({ item: renameItem, name });
    };
    const moveMut = useMutation({
        mutationFn: ({ item, dest }: { item: QuoteDriveItem; dest: string }) =>
            dataProvider.moveQuoteDriveItem(quoteId, item.path, dest),
        onSuccess: (_data, vars) => {
            invalidateQuoteDriveThumbnailCache(quoteId, vars.item.path);
            setMoveItem(null);
            invalidate();
        },
        onError: (err: Error) => notify(err.message, { type: 'error' }),
    });
    const deleteMut = useMutation({
        mutationFn: (item: QuoteDriveItem) =>
            dataProvider.deleteQuoteDriveItem(quoteId, item.path),
        onSuccess: async (_data, item) => {
            invalidateQuoteDriveThumbnailCache(quoteId, item.path);
            setDeleteItem(null);
            await refreshDriveList('Refreshing folder list…');
        },
        onError: (err: Error) => notify(err.message, { type: 'error' }),
    });
    const mapMut = useMutation({
        mutationFn: () => dataProvider.mapQuoteSharePointFiles(quoteId),
        onSuccess: (data: MapSharePointFilesResult) => {
            const created = Number(data.created ?? 0);
            const skipped = Number(data.skipped ?? 0);
            const removed = Number(data.removed ?? 0);
            const removedNote =
                removed > 0
                    ? `, removed ${removed} missing file${removed === 1 ? '' : 's'}`
                    : '';
            notify(
                created > 0
                    ? `Mapped ${created} file${created === 1 ? '' : 's'} (${skipped} already linked${removedNote})`
                    : removed > 0
                      ? `Removed ${removed} missing file${removed === 1 ? '' : 's'}`
                      : skipped > 0
                        ? 'All SharePoint files already linked'
                        : 'No files found in SharePoint folder',
                { type: created > 0 || removed > 0 ? 'success' : 'info' },
            );
            invalidate();
        },
        onError: (err: Error) => notify(err.message, { type: 'error' }),
    });
    const shareMut = useMutation({
        mutationFn: (item: QuoteDriveItem) =>
            dataProvider.createQuoteDriveShareLink(quoteId, item.path),
        onSuccess: (response: { share_link: string }) => {
            setShareLink(response.share_link);
        },
        onError: (err: Error) => {
            const message = err.message || 'Failed to generate sharing link';
            if (
                message === 'OneDrive request failed.' ||
                message === 'Failed to generate sharing link' ||
                message === 'Request failed'
            ) {
                console.error('OneDrive share link failed:', err);
            }
            notify(message, { type: 'error' });
            setShareItem(null);
        },
    });

    const rootPath = defaultRootPath ?? '';
    const relativePath = rootPath && path.startsWith(rootPath)
        ? path.slice(rootPath.length).replace(/^\//, '')
        : (!rootPath ? path : '');
    const crumbs = relativePath ? relativePath.split('/') : [];
    const canCreateActions = !readOnly && canCreate;
    const canUpdateActions = !readOnly && canUpdate;
    const canDeleteActions = !readOnly && canDelete;
    const conflictLocked = conflict ? Boolean(existingFile(conflict.name)?.locked) : false;

    const openItem = (item: QuoteDriveItem) => {
        if (item.is_folder) {
            setPath(item.path);
            return;
        }
        setPreview(driveItemToQuoteAttachment(quoteId, item));
    };

    const handleDownload = (item: QuoteDriveItem) => {
        showProgress(item.is_folder ? 'Downloading folder…' : 'Downloading file…');
        if (item.is_folder) {
            void dataProvider
                .downloadQuoteDriveZip(quoteId, [item.path])
                .then((blob: Blob) =>
                    triggerBlobDownload(blob, driveZipDownloadFilename([item.path])),
                )
                .catch((err: unknown) => {
                    notify(err instanceof Error ? err.message : 'Download failed', {
                        type: 'error',
                    });
                })
                .finally(() => hideProgress());
            return;
        }
        void downloadQuoteAttachment(driveItemToQuoteAttachment(quoteId, item))
            .catch(err => {
                notify(err instanceof Error ? err.message : 'Download failed', { type: 'error' });
            })
            .finally(() => hideProgress());
    };

    const handleBulkDownload = () => {
        const paths = [...selectedPaths];
        if (paths.length === 0) return;
        showProgress(
            paths.length === 1 ? 'Downloading item…' : `Downloading ${paths.length} items…`,
        );
        void dataProvider
            .downloadQuoteDriveZip(quoteId, paths)
            .then((blob: Blob) => triggerBlobDownload(blob, driveZipDownloadFilename(paths)))
            .catch((err: unknown) => {
                notify(err instanceof Error ? err.message : 'Download failed', { type: 'error' });
            })
            .finally(() => hideProgress());
    };

    const handleBulkDeleteConfirm = async () => {
        const paths = [...selectedPaths];
        const selectedItems = paths
            .map(p => items.find(item => item.path === p))
            .filter((item): item is QuoteDriveItem => item != null);
        const deletable = selectedItems.filter(item => !item.locked);
        const skippedLocked = selectedItems.length - deletable.length;

        setBulkBusy(true);
        let failed = 0;
        for (const item of deletable) {
            try {
                await dataProvider.deleteQuoteDriveItem(quoteId, item.path);
                invalidateQuoteDriveThumbnailCache(quoteId, item.path);
            } catch {
                failed += 1;
            }
        }
        setBulkBusy(false);
        setBulkDeleteOpen(false);
        clearSelection();
        if (deletable.length > 0) {
            await refreshDriveList('Refreshing folder list…');
        }

        if (failed > 0) {
            notify(
                `Deleted ${deletable.length - failed} item(s); ${failed} failed`,
                { type: 'warning' },
            );
        } else if (skippedLocked > 0) {
            notify(
                `Deleted ${deletable.length} item(s); skipped ${skippedLocked} locked`,
                { type: 'info' },
            );
        } else if (deletable.length > 0) {
            notify(`Deleted ${deletable.length} item(s)`, { type: 'success' });
        }
    };

    const handleShare = (item: QuoteDriveItem) => {
        setShareItem(item);
        setShareLink(null);
        shareMut.mutate(item);
    };

    const handleCopyLink = async () => {
        if (!shareLink) return;
        try {
            await navigator.clipboard.writeText(shareLink);
            setCopySuccess(true);
        } catch (err) {
            notify('Failed to copy link to clipboard', { type: 'error' });
        }
    };

    const enqueueUploads = (files: File[]) => {
        if (files.length === 0) return;
        uploadQueueRef.current.push(...files);
        if (!conflict && !uploadMut.isPending) {
            drainUploadQueue();
        }
    };

    const resolveConflictOverride = () => {
        if (!conflict || conflictLocked) return;
        startUpload(conflict.file, conflict.name, true);
    };

    const resolveConflictRename = () => {
        if (!conflict) return;
        const name = conflictName.trim();
        if (!name) return;
        startUpload(conflict.file, name, false);
    };

    const renderCard = (item: QuoteDriveItem) => {
        const allowDownload = canRead;
        const allowUpdate = canUpdateActions && !item.locked;
        const allowDelete = canDeleteActions && !item.locked;
        return (
            <Box
                key={item.path}
                sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    border: '1px solid',
                    borderColor: selectedPaths.has(item.path) ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    overflow: 'hidden',
                    '&:hover': { borderColor: 'primary.main' },
                }}
            >
                {canSelect && (
                    <Checkbox
                        size="small"
                        checked={selectedPaths.has(item.path)}
                        onClick={(event: MouseEvent<HTMLButtonElement>) => event.stopPropagation()}
                        onChange={() => toggleSelected(item.path)}
                        inputProps={{ 'aria-label': `Select ${item.name}` }}
                        sx={{ position: 'absolute', top: 4, left: 4, zIndex: 1 }}
                    />
                )}
                <Box
                    component="button"
                    type="button"
                    onClick={() => openItem(item)}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        border: 0,
                        bgcolor: 'transparent',
                        p: 0,
                        cursor: 'pointer',
                        textAlign: 'left',
                        flex: 1,
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            height: 96,
                            bgcolor: 'grey.100',
                            overflow: 'hidden',
                            position: 'relative',
                        }}
                    >
                        {item.is_folder ? (
                            <FolderIcon sx={{ fontSize: 48, color: 'warning.main' }} aria-hidden />
                        ) : (
                            <FileThumb item={item} quoteId={quoteId} />
                        )}
                    </Box>
                    <Typography
                        variant="caption"
                        sx={{
                            px: 1,
                            py: 0.75,
                            pr: 4,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                        title={item.name}
                    >
                        {item.name}
                    </Typography>
                </Box>
                <ItemMenu
                    item={item}
                    allowDownload={allowDownload}
                    allowUpdate={allowUpdate}
                    allowDelete={allowDelete}
                    onDownload={() => handleDownload(item)}
                    onShare={() => handleShare(item)}
                    onRename={() => {
                        setRenameItem(item);
                        setRenameValue(item.name);
                    }}
                    onMove={() => {
                        setMoveItem(item);
                        setMoveDest(path);
                    }}
                    onDelete={() => setDeleteItem(item)}
                />
            </Box>
        );
    };

    const renderRow = (item: QuoteDriveItem) => {
        const allowDownload = canRead;
        const allowUpdate = canUpdateActions && !item.locked;
        const allowDelete = canDeleteActions && !item.locked;
        return (
            <Box
                key={item.path}
                sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    border: '1px solid',
                    borderColor: selectedPaths.has(item.path) ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                    '&:hover': { borderColor: 'primary.main' },
                }}
            >
                {canSelect && (
                    <Checkbox
                        size="small"
                        checked={selectedPaths.has(item.path)}
                        onClick={(event: MouseEvent<HTMLButtonElement>) => event.stopPropagation()}
                        onChange={() => toggleSelected(item.path)}
                        inputProps={{ 'aria-label': `Select ${item.name}` }}
                    />
                )}
                <Box
                    component="button"
                    type="button"
                    onClick={() => openItem(item)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flex: 1,
                        border: 0,
                        bgcolor: 'transparent',
                        cursor: 'pointer',
                        textAlign: 'left',
                        minWidth: 0,
                        py: 0.5,
                    }}
                >
                    {item.is_folder ? (
                        <FolderIcon sx={{ color: 'warning.main' }} fontSize="small" />
                    ) : (
                        <FileThumb item={item} quoteId={quoteId} size={22} />
                    )}
                    <Typography
                        variant="body2"
                        noWrap
                        title={item.name}
                        sx={{ flex: 1 }}
                    >
                        {item.name}
                    </Typography>
                </Box>
                <ItemMenu
                    item={item}
                    allowDownload={allowDownload}
                    allowUpdate={allowUpdate}
                    allowDelete={allowDelete}
                    floating={false}
                    onDownload={() => handleDownload(item)}
                    onShare={() => handleShare(item)}
                    onRename={() => {
                        setRenameItem(item);
                        setRenameValue(item.name);
                    }}
                    onMove={() => {
                        setMoveItem(item);
                        setMoveDest(path);
                    }}
                    onDelete={() => setDeleteItem(item)}
                />
            </Box>
        );
    };

    if (!canRead) {
        return (
            <Typography variant="body2" color="text.secondary">
                You do not have permission to view project files.
            </Typography>
        );
    }

    return (
        <Box sx={{ position: 'relative' }}>
            <Box
                display="flex"
                alignItems="center"
                gap={1}
                flexWrap="wrap"
                sx={{ mb: 1.5 }}
            >
                {canCreateActions && (
                    <>
                        <Button
                            size="small"
                            startIcon={<UploadFileIcon />}
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadMut.isPending}
                        >
                            Upload
                        </Button>
                        <Button
                            size="small"
                            startIcon={<CreateNewFolderIcon />}
                            onClick={() => setFolderOpen(true)}
                        >
                            New folder
                        </Button>
                        <Button
                            size="small"
                            startIcon={
                                mapMut.isPending ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <CloudSyncIcon />
                                )
                            }
                            onClick={() => mapMut.mutate()}
                            disabled={mapMut.isPending}
                        >
                            Sync SharePoint files
                        </Button>
                    </>
                )}
                <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    flexWrap="wrap"
                    sx={{ ml: 'auto' }}
                >
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={view}
                        onChange={(_, next: ViewMode | null) => {
                            if (next) setView(next);
                        }}
                    >
                        <ToggleButton value="icons" aria-label="Icons">
                            <ViewModuleIcon fontSize="small" />
                        </ToggleButton>
                        <ToggleButton value="list" aria-label="List">
                            <ViewListIcon fontSize="small" />
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <IconButton
                        size="small"
                        aria-label="Toggle search"
                        aria-pressed={searchOpen}
                        color={searchOpen ? 'primary' : 'default'}
                        onClick={() => {
                            setSearchOpen(open => {
                                if (open) setSearchQuery('');
                                return !open;
                            });
                        }}
                    >
                        <SearchIcon fontSize="small" />
                    </IconButton>
                    {searchOpen && (
                        <TextField
                            size="small"
                            placeholder="Search in folder"
                            value={searchQuery}
                            onChange={event => setSearchQuery(event.target.value)}
                            autoFocus
                            sx={{ minWidth: 160 }}
                            inputProps={{ 'aria-label': 'Search files in folder' }}
                        />
                    )}
                </Box>
                <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    multiple
                    onChange={event => {
                        const files = Array.from(event.target.files ?? []);
                        event.target.value = '';
                        if (files.length > 0) {
                            showProgress('Uploading…');
                            enqueueUploads(files);
                        }
                    }}
                />
            </Box>

            <Breadcrumbs sx={{ mb: 1.5 }}>
                <Link
                    component="button"
                    type="button"
                    underline="hover"
                    color={crumbs.length > 0 ? 'inherit' : 'text.primary'}
                    onClick={() => setPath(rootPath || '')}
                >
                    {rootLabel ?? 'Files'}
                </Link>
                {crumbs.map((part, index) => {
                    const crumbPath = rootPath
                        ? `${rootPath}/${crumbs.slice(0, index + 1).join('/')}`
                        : crumbs.slice(0, index + 1).join('/');
                    const last = index === crumbs.length - 1;
                    return last ? (
                        <Typography key={crumbPath} color="text.primary">
                            {part}
                        </Typography>
                    ) : (
                        <Link
                            key={crumbPath}
                            component="button"
                            type="button"
                            underline="hover"
                            color="inherit"
                            onClick={() => setPath(crumbPath)}
                        >
                            {part}
                        </Link>
                    );
                })}
            </Breadcrumbs>

            {canSelect && selectedPaths.size > 0 && (
                <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    flexWrap="wrap"
                    sx={{ mb: 1.5 }}
                >
                    <Typography variant="body2">{selectedPaths.size} selected</Typography>
                    {canRead && (
                        <Button size="small" onClick={handleBulkDownload}>
                            Download
                        </Button>
                    )}
                    {canDeleteActions && (
                        <Button
                            size="small"
                            color="error"
                            onClick={() => setBulkDeleteOpen(true)}
                        >
                            Delete
                        </Button>
                    )}
                    <Button size="small" onClick={selectAllVisible}>
                        Select all
                    </Button>
                    <Button size="small" onClick={clearSelection}>
                        Clear
                    </Button>
                </Box>
            )}

            {/* Drag-and-drop overlay */}
            {dragActive && selectedPaths.size === 0 && canCreateActions && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: 1,
                        border: '2px dashed',
                        borderColor: 'primary.main',
                        zIndex: 10,
                        pointerEvents: 'none',
                    }}
                >
                    <Typography variant="h6" color="primary">
                        Drop here the file
                    </Typography>
                </Box>
            )}

            {driveQuery.isPending && (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress size={28} />
                </Box>
            )}
            {driveQuery.error && (
                <Typography color="error" variant="body2">
                    {driveQuery.error instanceof Error
                        ? driveQuery.error.message
                        : 'Failed to load files'}
                </Typography>
            )}
            {driveQuery.data && !driveQuery.data.configured && (
                <Typography variant="body2" color="text.secondary">
                    OneDrive is not configured.
                </Typography>
            )}
            {driveQuery.data?.configured && items.length === 0 && !driveQuery.isPending && (
                <Typography variant="body2" color="text.secondary">
                    No files in this folder.
                </Typography>
            )}
            {driveQuery.data?.configured &&
                items.length > 0 &&
                visibleItems.length === 0 &&
                !driveQuery.isPending && (
                    <Typography variant="body2" color="text.secondary">
                        No files match your search.
                    </Typography>
                )}
            {view === 'icons' ? (
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(auto-fill, minmax(120px, 1fr))"
                    gap={1.5}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    {visibleItems.map(renderCard)}
                </Box>
            ) : (
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={1}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    {visibleItems.map(renderRow)}
                </Box>
            )}

            <QuoteAttachmentPreviewDialog
                attachment={preview}
                onClose={() => setPreview(null)}
            />

            <Dialog
                open={folderOpen}
                onClose={() => {
                    if (!folderMut.isPending) setFolderOpen(false);
                }}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>New folder</DialogTitle>
                <DialogContent>
                    <TextField
                        inputRef={folderInputRef}
                        fullWidth
                        margin="dense"
                        label="Folder name"
                        value={folderName}
                        disabled={folderMut.isPending}
                        onChange={e => setFolderName(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                submitCreateFolder();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button disabled={folderMut.isPending} onClick={() => setFolderOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!folderName.trim() || folderMut.isPending}
                        startIcon={
                            folderMut.isPending ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : undefined
                        }
                        onClick={submitCreateFolder}
                    >
                        {folderMut.isPending ? 'Creating…' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={renameItem != null}
                onClose={() => {
                    if (!renameMut.isPending) setRenameItem(null);
                }}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Rename</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        margin="dense"
                        label="Name"
                        value={renameValue}
                        disabled={renameMut.isPending}
                        onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                submitRename();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button disabled={renameMut.isPending} onClick={() => setRenameItem(null)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!renameValue.trim() || renameMut.isPending || !renameItem}
                        startIcon={
                            renameMut.isPending ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : undefined
                        }
                        onClick={submitRename}
                    >
                        {renameMut.isPending ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={moveItem != null}
                onClose={() => setMoveItem(null)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Move to folder</DialogTitle>
                <DialogContent>
                    <List dense>
                        <ListItemButton
                            selected={moveDest === ''}
                            disabled={
                                moveItem != null && invalidMoveDest(moveItem, '')
                            }
                            onClick={() => setMoveDest('')}
                        >
                            <FolderIcon sx={{ mr: 1, color: 'warning.main' }} fontSize="small" />
                            <ListItemText primary="Files (root)" />
                        </ListItemButton>
                        <FolderTreePicker
                            nodes={treeQuery.data?.tree ?? []}
                            selected={moveDest}
                            onSelect={setMoveDest}
                            disabledPath={dest =>
                                moveItem != null && invalidMoveDest(moveItem, dest)
                            }
                        />
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMoveItem(null)}>Cancel</Button>
                    <Button
                        disabled={moveMut.isPending || !moveItem}
                        onClick={() => {
                            if (moveItem) {
                                moveMut.mutate({ item: moveItem, dest: moveDest });
                            }
                        }}
                    >
                        Move
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteItem != null}
                onClose={() => {
                    if (!deleteMut.isPending) setDeleteItem(null);
                }}
            >
                <DialogTitle>Delete {deleteItem?.is_folder ? 'folder' : 'file'}?</DialogTitle>
                <DialogContent>
                    <Typography>
                        {deleteItem?.is_folder
                            ? `Delete “${deleteItem.name}” and its contents from OneDrive?`
                            : `Delete “${deleteItem?.name}” from OneDrive?`}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button disabled={deleteMut.isPending} onClick={() => setDeleteItem(null)}>
                        Cancel
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={deleteMut.isPending || !deleteItem}
                        startIcon={
                            deleteMut.isPending ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : undefined
                        }
                        onClick={() => {
                            if (deleteItem) deleteMut.mutate(deleteItem);
                        }}
                    >
                        {deleteMut.isPending ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={bulkDeleteOpen}
                onClose={() => {
                    if (!bulkBusy) setBulkDeleteOpen(false);
                }}
            >
                <DialogTitle>Delete selected items?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Delete {selectedPaths.size} selected item
                        {selectedPaths.size === 1 ? '' : 's'} from OneDrive?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button disabled={bulkBusy} onClick={() => setBulkDeleteOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={bulkBusy}
                        startIcon={
                            bulkBusy ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : undefined
                        }
                        onClick={() => void handleBulkDeleteConfirm()}
                    >
                        {bulkBusy ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={conflict != null}
                onClose={() => {
                    if (uploadMut.isPending) return;
                    clearConflict();
                    drainUploadQueue();
                }}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>File already exists</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: conflictRename || conflictLocked ? 2 : 0 }}>
                        “{conflict?.name}” already exists in this folder.
                        {conflictLocked
                            ? ' This file is locked and cannot be overwritten.'
                            : ' Override it, or upload under a new name.'}
                    </Typography>
                    {conflictRename && (
                        <TextField
                            autoFocus
                            fullWidth
                            margin="dense"
                            label="New file name"
                            value={conflictName}
                            onChange={e => setConflictName(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    resolveConflictRename();
                                }
                            }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        disabled={uploadMut.isPending}
                        onClick={() => {
                            clearConflict();
                            drainUploadQueue();
                        }}
                    >
                        Cancel
                    </Button>
                    {conflictRename ? (
                        <Button
                            disabled={!conflictName.trim() || uploadMut.isPending}
                            onClick={resolveConflictRename}
                        >
                            Upload
                        </Button>
                    ) : (
                        <>
                            <Button
                                disabled={uploadMut.isPending}
                                onClick={() => {
                                    setConflictRename(true);
                                    setConflictName(conflict?.name ?? '');
                                }}
                            >
                                Rename
                            </Button>
                            <Button
                                color="warning"
                                disabled={uploadMut.isPending || conflictLocked}
                                onClick={resolveConflictOverride}
                            >
                                Override
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog
                open={shareItem != null}
                onClose={() => {
                    if (!shareMut.isPending) {
                        setShareItem(null);
                        setShareLink(null);
                    }
                }}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Share {shareItem?.name}</DialogTitle>
                <DialogContent>
                    {shareMut.isPending && (
                        <Box display="flex" justifyContent="center" py={2}>
                            <CircularProgress size={28} />
                        </Box>
                    )}
                    {shareLink && (
                        <Box display="flex" flexDirection="column" gap={2}>
                            <Typography variant="body2" color="text.secondary">
                                Anyone with this link can view and download this file
                                (served by the dashboard, no OneDrive login required).
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                value={shareLink}
                                InputProps={{
                                    readOnly: true,
                                }}
                                label="Sharing link"
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setShareItem(null);
                            setShareLink(null);
                        }}
                        disabled={shareMut.isPending}
                    >
                        Close
                    </Button>
                    {shareLink && (
                        <Button
                            variant="contained"
                            startIcon={<ContentCopyIcon />}
                            onClick={() => void handleCopyLink()}
                        >
                            Copy to Clipboard
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Snackbar
                open={progressMessage != null}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity="info"
                    variant="filled"
                    icon={<CircularProgress size={16} color="inherit" />}
                    sx={{
                        alignItems: 'center',
                        minWidth: 280,
                        boxShadow: 3,
                    }}
                >
                    {progressMessage}
                </Alert>
            </Snackbar>

            <Snackbar
                open={copySuccess}
                autoHideDuration={3000}
                onClose={() => setCopySuccess(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" variant="filled" sx={{ boxShadow: 3 }}>
                    Link copied to clipboard
                </Alert>
            </Snackbar>
        </Box>
    );
}
